import { Box, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip'

import { Text } from '@mxenabled/mxui'

import { LineChartLabel, TooltipLabel } from '../LineChart'
import { getMarkerShape, MARKER_SHAPE_PATHS } from './utils/markerShapes'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

interface CustomTooltipProps {
  customLabels?: TooltipLabel[][]
  isCurrency?: boolean
  labels?: LineChartLabel[]
  series: Array<{ id?: string; data: number[]; color?: string }>
  valueFormatterString?: string
}

export function CustomTooltip({
  customLabels,
  isCurrency = true,
  labels = [],
  series,
  valueFormatterString = '0,0',
}: CustomTooltipProps) {
  const tooltipData = useAxesTooltip()
  const theme = useTheme()

  const firstAxisData = tooltipData?.[0]

  if (!firstAxisData) return null

  const { axisValue, dataIndex = 0 } = firstAxisData

  if (customLabels) {
    const headerText = customLabels[dataIndex]?.[0]?.header || String(axisValue)

    return (
      <Stack
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12)',
          flexDirection: 'column',
          alignItems: 'flex-start',
          borderRadius: '4px',
          width: 250,
          gap: 8,
          px: 16,
          py: 12,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: 8,
            marginTop: 4,
          }}
        >
          <Text variant="h3">{headerText}</Text>
          <Text bold={true} variant="h3">
            {formatCurrency(series[0]?.data[dataIndex] as number, valueFormatterString)}
          </Text>
        </Stack>
        {customLabels[dataIndex]?.some((labelItem) => {
          const amount = typeof labelItem === 'string' ? 0 : labelItem.amount
          return amount !== 0
        }) ? (
          customLabels[dataIndex]?.map((labelItem) => {
            const label = typeof labelItem === 'string' ? labelItem : labelItem.label
            const amount = typeof labelItem === 'string' ? 0 : labelItem.amount

            if (amount === 0) return null
            return (
              <Stack
                key={label}
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: series[0]?.color,
                      borderRadius: '4px',
                    }}
                  />
                  <Text variant="body1">{label}</Text>
                </Stack>
                <Text bold={true} variant="body1">
                  {formatCurrency(Math.abs(amount), valueFormatterString)}
                </Text>
              </Stack>
            )
          })
        ) : (
          <Text variant="body1">No data</Text> //TODO: localize
        )}
      </Stack>
    )
  }
  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12)',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '4px',
        width: 225,
        gap: 8,
        px: 16,
        py: 12,
      }}
    >
      <Text variant="h3">{String(axisValue)}</Text>
      {series.map((s, sIndex) => (
        <Stack
          key={`${s.id}-${sIndex}`}
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: 8,
            marginTop: 4,
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <svg aria-hidden="true" height={12} viewBox="-6 -6 12 12" width={12}>
              <path d={MARKER_SHAPE_PATHS[getMarkerShape(sIndex)]} fill={s.color} />
            </svg>
            <Text variant="body1">
              {typeof labels[sIndex] === 'string' ? labels[sIndex] : labels[sIndex]?.label}
            </Text>
          </Stack>
          <Text bold={true} variant="body1">
            {isCurrency
              ? formatCurrency(s.data[dataIndex] as number, valueFormatterString)
              : s.data[dataIndex]}
          </Text>
        </Stack>
      ))}
    </Stack>
  )
}
